import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProject } from "@/hooks/useProject";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Download, Plus, Trash2, Edit, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

interface VariableManagerProps {
  templateId: number;
  pptFilePath: string | null;
}

export function VariableManager({ templateId, pptFilePath }: VariableManagerProps) {
  const [isExtracting, setIsExtracting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedVariable, setSelectedVariable] = useState<any>(null);
  const [selectedVariables, setSelectedVariables] = useState<Set<number>>(new Set());
  const [editForm, setEditForm] = useState({ description: "" });
  const [addForm, setAddForm] = useState({ variable_name: "", description: "" });
  const queryClient = useQueryClient();
  const { projectClient, currentProject } = useProject();

  // Fetch variables from database
  const { data: variables, isLoading } = useQuery({
    queryKey: ["template_variables_v2", currentProject, templateId],
    queryFn: async () => {
      const { data, error } = await projectClient
        .from("template_variables_v2")
        .select("*")
        .eq("template_id", templateId)
        .eq("project_id", projectClient.getProjectId())
        .order("variable_name");

      if (error) throw error;
      return data || [];
    },
    enabled: !!templateId,
  });

  // Extract variables from PPT
  const handleExtractVariables = async () => {
    if (!pptFilePath) {
      toast.error("No hay archivo PPT vinculado a esta plantilla");
      return;
    }

    setIsExtracting(true);
    try {
      const { data, error } = await supabase.functions.invoke('extract-ppt-variables', {
        body: {
          file_path: pptFilePath,
          template_id: templateId,
          project_id: projectClient.getProjectId(),
        }
      });

      if (error) throw error;

      toast.success(`✅ ${data.count} variables extraídas del PPT`);
      queryClient.invalidateQueries({ queryKey: ["template_variables_v2", currentProject, templateId] });
    } catch (error: any) {
      toast.error(`Error al extraer variables: ${error.message}`);
    } finally {
      setIsExtracting(false);
    }
  };

  // Delete variable mutation
  const deleteMutation = useMutation({
    mutationFn: async (variableId: number) => {
      const { error } = await projectClient
        .from("template_variables_v2")
        .delete()
        .eq("id", variableId)
        .eq("project_id", projectClient.getProjectId());

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Variable eliminada");
      queryClient.invalidateQueries({ queryKey: ["template_variables_v2", currentProject, templateId] });
      setDeleteDialogOpen(false);
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.message}`);
    },
  });

  // Update variable mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, description }: { id: number; description: string }) => {
      const { error } = await projectClient
        .from("template_variables_v2")
        .update({ description, updated_at: new Date().toISOString() })
        .eq("id", id)
        .eq("project_id", projectClient.getProjectId());

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Variable actualizada");
      queryClient.invalidateQueries({ queryKey: ["template_variables_v2", currentProject, templateId] });
      setEditDialogOpen(false);
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.message}`);
    },
  });

  // Add custom variable mutation
  const addMutation = useMutation({
    mutationFn: async ({ variable_name, description }: { variable_name: string; description: string }) => {
      const { error } = await projectClient
        .from("template_variables_v2")
        .insert({
          template_id: templateId,
          project_id: projectClient.getProjectId(),
          variable_name,
          description,
          is_custom: true,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Variable agregada");
      queryClient.invalidateQueries({ queryKey: ["template_variables_v2", currentProject, templateId] });
      setAddDialogOpen(false);
      setAddForm({ variable_name: "", description: "" });
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.message}`);
    },
  });

  // Bulk delete variables mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: async (variableIds: number[]) => {
      const { error } = await projectClient
        .from("template_variables_v2")
        .delete()
        .in("id", variableIds)
        .eq("project_id", projectClient.getProjectId());

      if (error) throw error;
    },
    onSuccess: () => {
      const count = selectedVariables.size;
      toast.success(`${count} variable${count > 1 ? 's' : ''} eliminada${count > 1 ? 's' : ''}`);
      queryClient.invalidateQueries({ queryKey: ["template_variables_v2", currentProject, templateId] });
      setBulkDeleteDialogOpen(false);
      setSelectedVariables(new Set());
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.message}`);
    },
  });

  // Selection handlers
  const handleSelectVariable = (variableId: number, checked: boolean) => {
    const newSelection = new Set(selectedVariables);
    if (checked) {
      newSelection.add(variableId);
    } else {
      newSelection.delete(variableId);
    }
    setSelectedVariables(newSelection);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(variables?.map(v => v.id) || []);
      setSelectedVariables(allIds);
    } else {
      setSelectedVariables(new Set());
    }
  };

  const isAllSelected = variables?.length > 0 && selectedVariables.size === variables.length;
  const isIndeterminate = selectedVariables.size > 0 && selectedVariables.size < (variables?.length || 0);

  const openEditDialog = (variable: any) => {
    setSelectedVariable(variable);
    setEditForm({ description: variable.description || "" });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (variable: any) => {
    setSelectedVariable(variable);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                Variables de la plantilla
                <Badge variant="secondary" className="ml-2">
                  {variables?.length || 0}
                </Badge>
              </CardTitle>
              <CardDescription>
                Variables extraídas del archivo PPT o agregadas manualmente
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {selectedVariables.size > 0 && (
                <Button
                  variant="destructive"
                  onClick={() => setBulkDeleteDialogOpen(true)}
                  disabled={selectedVariables.size === 0}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Eliminar seleccionadas ({selectedVariables.size})
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setAddDialogOpen(true)}
                disabled={!pptFilePath}
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar variable
              </Button>
              <Button
                onClick={handleExtractVariables}
                disabled={!pptFilePath || isExtracting}
              >
                {isExtracting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Extrayendo...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Cargar variables del PPT
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!pptFilePath && (
            <div className="text-sm text-muted-foreground mb-4 p-4 border border-yellow-200 bg-yellow-50 rounded">
              ⚠️ Primero debes subir un archivo PPT en la pestaña de edición de plantilla
            </div>
          )}
          {isLoading ? (
            <div className="text-center py-8">Cargando variables...</div>
          ) : !variables || variables.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No hay variables extraídas. Haz clic en "Cargar variables del PPT" para comenzar.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={isAllSelected ? true : isIndeterminate ? "indeterminate" : false}
                      onCheckedChange={handleSelectAll}
                      aria-label="Seleccionar todas las variables"
                    />
                  </TableHead>
                  <TableHead>Variable</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead>Origen</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {variables.map((variable) => (
                  <TableRow key={variable.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedVariables.has(variable.id)}
                        onCheckedChange={(checked) => handleSelectVariable(variable.id, checked as boolean)}
                        aria-label={`Seleccionar variable ${variable.variable_name}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {variable.variable_name}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{variable.variable_type}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {variable.description || "Sin descripción"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={variable.is_custom ? "default" : "secondary"}>
                        {variable.is_custom ? "Manual" : "Auto"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(variable)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteDialog(variable)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar variable</DialogTitle>
            <DialogDescription>
              Modifica la descripción de la variable: <code className="font-mono">{selectedVariable?.variable_name}</code>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={editForm.description}
                onChange={(e) => setEditForm({ description: e.target.value })}
                placeholder="Describe el propósito de esta variable..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() =>
                updateMutation.mutate({
                  id: selectedVariable?.id,
                  description: editForm.description,
                })
              }
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar variable?</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de eliminar la variable <code className="font-mono">{selectedVariable?.variable_name}</code>?
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate(selectedVariable?.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent className="max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>¿Eliminar variables seleccionadas?</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de eliminar {selectedVariables.size} variable{selectedVariables.size > 1 ? 's' : ''}?
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            <div className="text-sm mb-2">
              Variables a eliminar:
            </div>
            <div className="max-h-60 overflow-y-auto border rounded-md p-3 bg-muted/50">
              <ul className="list-disc ml-4 space-y-1">
                {variables?.filter(v => selectedVariables.has(v.id)).map(v => (
                  <li key={v.id} className="font-mono text-sm">{v.variable_name}</li>
                ))}
              </ul>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => bulkDeleteMutation.mutate(Array.from(selectedVariables))}
              disabled={bulkDeleteMutation.isPending}
            >
              {bulkDeleteMutation.isPending ? "Eliminando..." : `Eliminar ${selectedVariables.size} variable${selectedVariables.size > 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar variable personalizada</DialogTitle>
            <DialogDescription>
              Agrega una variable que no está en el archivo PPT
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="variable_name">Nombre de la variable</Label>
              <Input
                id="variable_name"
                value={addForm.variable_name}
                onChange={(e) => setAddForm({ ...addForm, variable_name: e.target.value })}
                placeholder="mi_variable"
              />
            </div>
            <div>
              <Label htmlFor="add_description">Descripción</Label>
              <Textarea
                id="add_description"
                value={addForm.description}
                onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                placeholder="Describe el propósito de esta variable..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => addMutation.mutate(addForm)}
              disabled={!addForm.variable_name.trim() || addMutation.isPending}
            >
              {addMutation.isPending ? "Agregando..." : "Agregar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
